export interface ExportSection {
  title: string;
  headers: string[];
  rows: (string | number)[][];
  titleStyle?: 'default' | 'employee' | 'cashier' | 'section' | 'cash';
}

export interface ExportConfig {
  reportTitle: string;
  periodLabel: string;
  restaurantName: string;
  timestamp: string;
  summaryCards?: { label: string; value: string }[];
  sections: ExportSection[];
}

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 150);
}

export function exportCSV(config: ExportConfig, filenamePrefix: string): void {
  // BOM for UTF-8 support in Excel (Spanish characters)
  let content = '\uFEFF';

  content += `${config.reportTitle} - ${config.periodLabel}\n`;
  content += `Generado: ${config.timestamp}\n`;
  content += `Restaurante: ${config.restaurantName}\n\n`;

  if (config.summaryCards && config.summaryCards.length > 0) {
    content += 'RESUMEN\n';
    config.summaryCards.forEach((card) => {
      content += `${card.label},${card.value}\n`;
    });
    content += '\n';
  }

  config.sections.forEach((section) => {
    if (section.rows.length === 0) return;
    content += `${section.title.toUpperCase()}\n`;
    content += section.headers.join(',') + '\n';
    section.rows.forEach((row) => {
      content += row.map((cell) => {
        const str = String(cell);
        // Escape commas and quotes in CSV
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',') + '\n';
    });
    content += '\n';
  });

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filenamePrefix}-${Date.now()}.csv`);
}

/**
 * Una tabla plana a CSV (con BOM para que Excel respete acentos). Para listas que se
 * cuadran contra otro sistema, p. ej. los tickets de una global contra el reporte de la
 * terminal.
 */
export function exportTablaCSV(headers: string[], rows: (string | number)[][], filenamePrefix: string): void {
  const celda = (cell: string | number) => {
    const str = String(cell ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const content = '﻿' + [headers, ...rows].map((row) => row.map(celda).join(',')).join('\n') + '\n';
  downloadBlob(new Blob([content], { type: 'text/csv;charset=utf-8;' }), `${filenamePrefix}-${Date.now()}.csv`);
}

function getTitleColor(style?: string): string {
  switch (style) {
    case 'employee': return '#9C27B0';
    case 'cashier': return '#FF9800';
    case 'cash': return '#00897B';
    case 'section': return '#2196F3';
    default: return '#4CAF50';
  }
}

export function exportExcel(config: ExportConfig, filenamePrefix: string): void {
  let html = `
    <html xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head>
      <meta charset="UTF-8">
      <style>
        table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { color: white; font-weight: bold; }
        .summary { background-color: #f2f2f2; font-weight: bold; }
      </style>
    </head>
    <body>
      <h2>${escapeHtml(config.reportTitle)} - ${escapeHtml(config.periodLabel)}</h2>
      <p><strong>Generado:</strong> ${escapeHtml(config.timestamp)}</p>
      <p><strong>Restaurante:</strong> ${escapeHtml(config.restaurantName)}</p>
  `;

  if (config.summaryCards && config.summaryCards.length > 0) {
    html += '<h3>Resumen</h3><table>';
    config.summaryCards.forEach((card) => {
      html += `<tr class="summary"><td>${escapeHtml(card.label)}</td><td>${escapeHtml(card.value)}</td></tr>`;
    });
    html += '</table>';
  }

  config.sections.forEach((section) => {
    if (section.rows.length === 0) return;
    const color = getTitleColor(section.titleStyle);
    html += `<h3>${escapeHtml(section.title)}</h3><table>`;
    html += '<tr>' + section.headers.map((h) => `<th style="background-color:${color}">${escapeHtml(h)}</th>`).join('') + '</tr>';
    section.rows.forEach((row) => {
      html += '<tr>' + row.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('') + '</tr>';
    });
    html += '</table>';
  });

  html += '</body></html>';

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  downloadBlob(blob, `${filenamePrefix}-${Date.now()}.xls`);
}

export function exportPDF(config: ExportConfig): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${escapeHtml(config.reportTitle)} - ${escapeHtml(config.periodLabel)}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #333; border-bottom: 2px solid #4CAF50; padding-bottom: 10px; }
        h2 { color: #666; margin-top: 30px; }
        table { border-collapse: collapse; width: 100%; margin-top: 15px; }
        th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
        th { color: white; font-weight: bold; }
        .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 15px; margin: 20px 0; }
        .summary-card { background: #f5f5f5; padding: 15px; border-radius: 8px; }
        .summary-label { font-size: 12px; color: #666; }
        .summary-value { font-size: 20px; font-weight: bold; color: #333; margin-top: 5px; }
        .header-info { background: #f9f9f9; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        @media print { .no-print { display: none; } }
      </style>
    </head>
    <body>
      <div class="header-info">
        <h1>${escapeHtml(config.reportTitle)} - ${escapeHtml(config.periodLabel)}</h1>
        <p><strong>Generado:</strong> ${escapeHtml(config.timestamp)}</p>
        <p><strong>Restaurante:</strong> ${escapeHtml(config.restaurantName)}</p>
      </div>
  `;

  if (config.summaryCards && config.summaryCards.length > 0) {
    html += '<h2>Resumen</h2><div class="summary-grid">';
    config.summaryCards.forEach((card) => {
      html += `<div class="summary-card"><div class="summary-label">${escapeHtml(card.label)}</div><div class="summary-value">${escapeHtml(card.value)}</div></div>`;
    });
    html += '</div>';
  }

  config.sections.forEach((section) => {
    if (section.rows.length === 0) return;
    const color = getTitleColor(section.titleStyle);
    html += `<h2>${escapeHtml(section.title)}</h2><table>`;
    html += '<tr>' + section.headers.map((h) => `<th style="background-color:${color}">${escapeHtml(h)}</th>`).join('') + '</tr>';
    section.rows.forEach((row) => {
      html += '<tr>' + row.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('') + '</tr>';
    });
    html += '</table>';
  });

  html += `
      <div class="no-print" style="margin-top: 30px; text-align: center;">
        <button onclick="window.print()" style="background: #4CAF50; color: white; border: none; padding: 15px 30px; font-size: 16px; border-radius: 5px; cursor: pointer;">
          Imprimir / Guardar como PDF
        </button>
        <button onclick="window.close()" style="background: #f44336; color: white; border: none; padding: 15px 30px; font-size: 16px; border-radius: 5px; cursor: pointer; margin-left: 10px;">
          Cerrar
        </button>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

export function getPeriodLabel(
  selectedPeriod: string,
  customDateRange?: [any, any] | null
): string {
  if (customDateRange && customDateRange[0] && customDateRange[1]) {
    return `${customDateRange[0].format('DD/MM/YYYY')} - ${customDateRange[1].format('DD/MM/YYYY')}`;
  }
  switch (selectedPeriod) {
    case 'today': return 'Hoy';
    case 'week': return 'Esta Semana';
    case 'month': return 'Este Mes';
    default: return 'Periodo Seleccionado';
  }
}
